import { useEffect, useMemo, useRef, useState } from 'react';

import {
  Box,
  Button,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper,
  type PopoverOrigin,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type {
  DateRange,
  PickersActionBarProps,
  PickersShortcutsItem,
} from '@mui/x-date-pickers-pro';
import {
  DateField,
  LocalizationProvider,
  usePickerContext,
  usePickerRangePositionContext,
  usePickerTranslations,
} from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFnsV2';
import { StaticDateRangePicker } from '@mui/x-date-pickers-pro/StaticDateRangePicker';
import { endOfDay, endOfWeek, format, startOfDay, startOfWeek } from 'date-fns';
import { CalendarDays, Check, ChevronDown } from 'lucide-react';

import { FilterChip } from '@modules/_shared/components/filters/quick-filters/FilterChip';
import {
  type DateFilterField,
  type QuickFilterProps,
} from '@modules/_shared/components/filters/quick-filters/QuickFilters';
import { useFiltersSearchParams } from '@modules/_shared/components/filters/useFiltersSearchParams';
import { fromDateStringToUTC, toUTCDate } from '@modules/_shared/utils/dates';
import {
  SQLIntervalToISODate,
  isSQLIntervalString,
} from '@modules/_shared/utils/sqlInterval';
import { type Translate, useLocale } from '@shared/hooks/locale';
import { customPalette } from '@shared/theme/StyleProvider';

// primary.main (blue500) as rgba
const PRIMARY_TINT_HOVER = 'rgba(82, 120, 223, 0.08)';
const PRIMARY_TINT_RANGE = 'rgba(82, 120, 223, 0.12)';

const SOFT_SHADOW =
  '0 12px 32px -8px rgba(16, 24, 40, 0.16), 0 4px 12px -4px rgba(16, 24, 40, 0.08)';

export const getStaticDateRangePickerSx = (
  isTablet: boolean,
  isMobile: boolean,
) => ({
  borderRadius: 3,
  bgcolor: 'background.paper',
  '& .MuiPickersLayout-root': {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : '150px auto',
    gridTemplateRows: isMobile ? 'auto auto auto auto' : 'auto auto auto',
    alignItems: 'start',
  },
  '.MuiDateRangeCalendar-root': {
    ...(isTablet && { margin: '0 auto' }),
  },
  '& .MuiPickersLayout-shortcuts': {
    gridColumn: 1,
    gridRow: 2,
    ...(isMobile
      ? {
          overflowY: 'auto',
          display: 'flex',
          margin: '0 auto',
          paddingTop: 1.5,
          paddingBottom: 2,
          maxWidth: 253,
          '& .MuiListItem-root': { paddingLeft: 0 },
          '& .MuiListItem-root:last-child': { paddingRight: 0 },
          '& .MuiChip-root': { borderRadius: '8px', fontWeight: 500 },
        }
      : {
          display: 'flex',
          flexDirection: 'column',
          gap: 0.25,
          padding: 1,
          borderRight: '1px solid',
          borderColor: 'grey.100',
          '& .MuiListItem-root': { padding: 0 },
          '& .MuiChip-root': {
            width: '100%',
            height: 34,
            justifyContent: 'flex-start',
            borderRadius: '8px',
            backgroundColor: 'transparent',
            color: 'text.secondary',
            fontWeight: 500,
            fontSize: '0.8125rem',
            '&:hover': { backgroundColor: 'grey.100', color: 'text.primary' },
            // keep focus visible (transparent base hides the default tint)
            '&.Mui-focusVisible': { backgroundColor: 'grey.100' },
          },
        }),
  },
  '& .MuiPickersLayout-contentWrapper': {
    gridColumn: isMobile ? 1 : 2,
    gridRow: isMobile ? 3 : 2,
    paddingRight: isMobile ? 0 : 2,
  },
  '& .MuiPickersLayout-actionBar': {
    gridColumn: isMobile ? 1 : 2,
    gridRow: isMobile ? 4 : 3,
  },
  '& .MuiPickersCalendarHeader-label': {
    fontWeight: 600,
    fontSize: '0.9375rem',
  },
  '& .MuiPickersArrowSwitcher-root': {
    ...((isTablet || isMobile) && { paddingTop: 0 }),
  },
  '& .MuiPickersArrowSwitcher-button': {
    borderRadius: '8px',
    color: 'text.secondary',
    '&:hover': { backgroundColor: 'grey.100' },
  },
  '& .MuiDayCalendar-weekDayLabel': {
    fontWeight: 600,
    fontSize: '0.6875rem',
    textTransform: 'uppercase',
    color: 'text.disabled',
  },
  // v9 range classes (v6's rangeIntervalDayHighlight was renamed)
  '& .MuiDateRangePickerDay-insideSelection': {
    backgroundColor: PRIMARY_TINT_RANGE,
  },
  '& .MuiDateRangePickerDay-today': {
    borderColor: 'primary.main',
  },
});

export const shortcutsItems: (
  t: Translate,
) => PickersShortcutsItem<DateRange<Date>>[] = (t: Translate) => [
  {
    label: t('Today'),
    getValue: () => {
      const today = new Date();
      return [startOfDay(today), endOfDay(today)];
    },
  },
  {
    label: t('Yesterday'),
    getValue: () => {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      return [startOfDay(yesterday), endOfDay(yesterday)];
    },
  },
  {
    label: t('Last week'),
    getValue: () => {
      const today = new Date();
      const lastWeek = new Date(today);
      lastWeek.setDate(today.getDate() - 7);
      return [
        startOfWeek(lastWeek, { weekStartsOn: 1 }),
        endOfWeek(lastWeek, { weekStartsOn: 1 }),
      ];
    },
  },
  {
    label: t('This month'),
    getValue: () => {
      const today = new Date();
      return [
        startOfDay(new Date(today.getFullYear(), today.getMonth(), 1)),
        endOfDay(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
      ];
    },
  },
  {
    label: t('Last 30 days'),
    getValue: () => {
      const today = new Date();
      const lastMonth = new Date(today);
      lastMonth.setDate(today.getDate() - 30);
      return [startOfDay(lastMonth), endOfDay(today)];
    },
  },
  {
    label: t('Last month'),
    getValue: () => {
      const today = new Date();
      const lastMonth = new Date(today);
      lastMonth.setMonth(today.getMonth() - 1);
      return [
        startOfDay(new Date(lastMonth.getFullYear(), lastMonth.getMonth(), 1)),
        endOfDay(
          new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0),
        ),
      ];
    },
  },
  {
    label: t('This year'),
    getValue: () => {
      const today = new Date();
      return [
        startOfDay(new Date(today.getFullYear(), 0, 1)),
        endOfDay(new Date(today.getFullYear(), 11, 31)),
      ];
    },
  },
];

const dateFieldBoxSx = { flexGrow: 1, pt: 1.5, px: 2 };
const dateFieldSx = {
  '& .MuiPickersInputBase-root, & .MuiOutlinedInput-root': {
    height: 40,
    borderRadius: '8px',
    backgroundColor: 'grey.50',
  },
  '& .MuiPickersInputBase-notchedOutline, & .MuiOutlinedInput-notchedOutline': {
    borderColor: 'grey.200',
  },
  '& .MuiInputAdornment-root': { display: 'none' },
  '& .MuiInputBase-input': {
    paddingTop: 1,
    paddingBottom: 1,
  },
};

export type CustomActionBarProps = PickersActionBarProps & {
  onCancel?: () => void;
};

export const CustomActionBar = ({
  className,
  onCancel,
}: CustomActionBarProps) => {
  const { acceptValueChanges, cancelValueChanges } = usePickerContext();
  const translations = usePickerTranslations();

  return (
    <Box
      className={className}
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 1,
        px: 2,
        pb: 2,
      }}
    >
      <Button
        color="inherit"
        sx={{ color: 'text.secondary' }}
        onClick={() => {
          cancelValueChanges();
          onCancel?.();
        }}
      >
        {translations.cancelButtonLabel}
      </Button>
      <Button variant="contained" onClick={acceptValueChanges}>
        {translations.okButtonLabel}
      </Button>
    </Box>
  );
};

export const CustomToolBar = ({ className }: { className?: string }) => {
  const { value, setValue, acceptValueChanges } = usePickerContext();
  const [from, to] = value as unknown as DateRange<Date>;
  const setRangeValue = setValue as unknown as (
    newValue: DateRange<Date>,
    options?: Parameters<typeof setValue>[1],
  ) => void;
  const { rangePosition, setRangePosition } = usePickerRangePositionContext();
  const { dateLocale } = useLocale();
  // Follow the locale's short date format (CH → dd.MM.yyyy, FR/IT → dd/MM/yyyy).
  const dateFieldFormat =
    dateLocale.formatLong?.date({ width: 'short' }) ?? 'dd/MM/yyyy';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Create refs for both date inputs
  const startInputRef = useRef<HTMLInputElement>(null);
  const endInputRef = useRef<HTMLInputElement>(null);

  // Automatically focus the appropriate input when the range position changes
  useEffect(() => {
    if (rangePosition === 'start' && startInputRef.current) {
      startInputRef.current.focus();
    } else if (rangePosition === 'end' && endInputRef.current) {
      endInputRef.current.focus();
    }
  }, [rangePosition]);

  return (
    <form
      className={className}
      style={{
        gridColumn: isMobile ? 1 : 2,
        gridRow: 1,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        paddingRight: isMobile ? 0 : 16,
      }}
      onSubmit={e => {
        e.preventDefault();
        acceptValueChanges();
      }}
    >
      <Box sx={dateFieldBoxSx}>
        <DateField
          inputRef={startInputRef}
          autoFocus={rangePosition === 'start'}
          value={from}
          format={dateFieldFormat}
          onChange={(date, { validationError }) => {
            if (!validationError) {
              setRangeValue([date, to], { changeImportance: 'set' });
            }
          }}
          variant="outlined"
          size="small"
          fullWidth
          onFocus={() => setRangePosition('start')}
          slotProps={{
            textField: {
              // disable autocomplete and autofill
              slotProps: { htmlInput: { autoComplete: 'one-time-code' } },
            },
          }}
          sx={dateFieldSx}
        />
      </Box>
      <Box sx={dateFieldBoxSx}>
        <DateField
          inputRef={endInputRef}
          autoFocus={rangePosition === 'end'}
          value={to}
          format={dateFieldFormat}
          onChange={(date, { validationError }) => {
            if (!validationError) {
              setRangeValue([from, date], { changeImportance: 'set' });
            }
          }}
          variant="outlined"
          size="small"
          fullWidth
          onFocus={() => setRangePosition('end')}
          slotProps={{
            textField: {
              // disable autocomplete and autofill
              slotProps: { htmlInput: { autoComplete: 'one-time-code' } },
            },
          }}
          sx={dateFieldSx}
        />
      </Box>
      <button type="submit" style={{ display: 'none' }} />
    </form>
  );
};

const parseDateOrInterval = (
  dateString: string,
  accountForTz: boolean,
): number => {
  if (isSQLIntervalString(dateString)) {
    return new Date(SQLIntervalToISODate(dateString)).getTime();
  }

  return accountForTz
    ? new Date(dateString).getTime()
    : fromDateStringToUTC(dateString).getTime();
};

export const extractDateRangeValues = (
  valueFromPath: any,
  accountForTz = true,
) => {
  const startDate = valueFromPath?._gte
    ? parseDateOrInterval(valueFromPath?._gte, accountForTz)
    : null;
  const endDate = valueFromPath?._lte
    ? parseDateOrInterval(valueFromPath?._lte, accountForTz)
    : null;

  return {
    startDate,
    endDate,
  };
};

// Shared container styling for the 3 date-range popovers.
export const dateRangePickerPaperSx = {
  borderRadius: 3,
  border: '1px solid',
  borderColor: 'grey.200',
  overflow: 'hidden',
  boxShadow: SOFT_SHADOW,
};

const dateFieldSelectorSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  px: 1.75,
  py: 1.25,
  borderBottom: '1px solid',
  borderColor: 'grey.100',
};
const fieldSelectButtonSx = {
  fontSize: '0.8125rem',
  color: 'text.primary',
  bgcolor: 'grey.50',
  border: '1px solid',
  borderColor: 'grey.200',
  borderRadius: '8px',
  px: 1,
  py: 0.25,
  minWidth: 0,
  '&:hover': { bgcolor: 'grey.100', borderColor: 'grey.300' },
  '& .MuiButton-endIcon': { ml: 0.5 },
};
const fieldMenuItemSx = {
  borderRadius: '6px',
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: 'text.secondary',
  '&.Mui-selected': {
    color: 'primary.main',
    fontWeight: 600,
    bgcolor: PRIMARY_TINT_HOVER,
    '&:hover': { bgcolor: PRIMARY_TINT_RANGE },
  },
};
const fieldMenuAnchorOrigin: PopoverOrigin = {
  vertical: 'bottom',
  horizontal: 'left',
};
const fieldMenuTransformOrigin: PopoverOrigin = {
  vertical: 'top',
  horizontal: 'left',
};
const fieldMenuPaperSx = {
  mt: 0.5,
  minWidth: 220,
  borderRadius: '8px',
  border: '1px solid',
  borderColor: 'grey.200',
  boxShadow: SOFT_SHADOW,
  '& .MuiList-root': { padding: 0.5 },
};
const filterByLabelSx = { color: 'text.secondary' };
const fieldMenuIconSx = { minWidth: 28, color: 'primary.main' };
const chevronBaseStyle = { transition: 'transform 0.15s ease' };
const chevronOpenStyle = { ...chevronBaseStyle, transform: 'rotate(180deg)' };
const chevronClosedStyle = { ...chevronBaseStyle, transform: 'none' };

const DateFieldSelector = ({
  fields,
  value,
  onChange,
}: {
  fields: DateFilterField[];
  value: string;
  onChange: (value: string) => void;
}) => {
  const { t } = useLocale();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const selected = fields.find(field => field.value === value) ?? fields[0];

  return (
    <Box sx={dateFieldSelectorSx}>
      <CalendarDays size={16} style={{ color: customPalette.disabledText }} />
      <Typography variant="body2" sx={filterByLabelSx}>
        {t('Filter by')}
      </Typography>
      <Button
        size="small"
        disableRipple
        onClick={event => setAnchorEl(event.currentTarget)}
        endIcon={
          <ChevronDown
            size={15}
            style={open ? chevronOpenStyle : chevronClosedStyle}
          />
        }
        sx={fieldSelectButtonSx}
      >
        {selected.label}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={fieldMenuAnchorOrigin}
        transformOrigin={fieldMenuTransformOrigin}
        slotProps={{ paper: { sx: fieldMenuPaperSx } }}
      >
        {fields.map(field => (
          <MenuItem
            key={field.value}
            selected={field.value === value}
            onClick={() => {
              onChange(field.value);
              setAnchorEl(null);
            }}
            sx={fieldMenuItemSx}
          >
            <ListItemIcon sx={fieldMenuIconSx}>
              {field.value === value ? <Check size={16} /> : null}
            </ListItemIcon>
            {field.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export const DateFilter = ({
  label,
  path,
  where,
  addWhereClause,
  getValueFromPath,
  getTypeFromPath,
  deleteWhereClause,
  queryParamsScope,
  disabled,
  type,
  dateFields,
  onChange,
}: QuickFilterProps) => {
  const [, setFiltersParams] = useFiltersSearchParams(queryParamsScope);
  const { dateLocale, t } = useLocale();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // When `dateFields` holds 2+ columns the filter can target one of them; the
  // selected column replaces the last segment of `path`. One column at a time.
  const hasFieldSelector = (dateFields?.length ?? 0) > 1;
  const basePath = path.slice(0, -1);
  const lastSegment = path[path.length - 1];
  const defaultFieldValue =
    hasFieldSelector &&
    dateFields &&
    !dateFields.some(field => field.value === lastSegment)
      ? dateFields[0].value
      : lastSegment;

  const activeFieldValue =
    dateFields?.find(
      field => getValueFromPath([...basePath, field.value], where) != null,
    )?.value ?? defaultFieldValue;
  const activePath = [...basePath, activeFieldValue];
  const activeType = getTypeFromPath(activePath) ?? type;
  const isDateType = activeType.name === 'date_comparison_exp';
  const { startDate, endDate } = extractDateRangeValues(
    getValueFromPath(activePath, where),
    !isDateType,
  );

  const [range, setRange] = useState<DateRange<Date>>([
    startDate ? new Date(startDate) : null,
    endDate ? new Date(endDate) : null,
  ]);
  const [rangePosition, setRangePosition] = useState<'start' | 'end'>('start');
  const [selectedField, setSelectedField] = useState(activeFieldValue);

  useEffect(() => {
    setRange([
      startDate ? new Date(startDate) : null,
      endDate ? new Date(endDate) : null,
    ]);
  }, [startDate, endDate]);

  useEffect(() => {
    setSelectedField(activeFieldValue);
  }, [activeFieldValue]);

  const resetDraft = () => {
    setRange([
      startDate ? new Date(startDate) : null,
      endDate ? new Date(endDate) : null,
    ]);
    setSelectedField(activeFieldValue);
  };

  // Clear only the previously-active column and the new target, so a date range
  // set on a sibling column elsewhere is left alone while switching fields still
  // drops the old clause.
  const clearTargeted = (current: any, targetField: string) => {
    if (!hasFieldSelector) {
      return deleteWhereClause(current, path, true);
    }
    const fieldsToClear = new Set([activeFieldValue, targetField]);
    return [...fieldsToClear].reduce(
      (acc, field) => deleteWhereClause(acc, [...basePath, field], true),
      current,
    );
  };

  const handleAccept = (
    handleClose: () => void,
    acceptedRange: DateRange<Date> = range,
  ) => {
    const targetPath = hasFieldSelector ? [...basePath, selectedField] : path;
    const targetIsDate =
      (getTypeFromPath(targetPath) ?? type).name === 'date_comparison_exp';

    let newWhere = clearTargeted(where, selectedField);
    let _gte: null | Date = null;
    let _lte: null | Date = null;

    if (acceptedRange[0]) {
      _gte = targetIsDate
        ? toUTCDate(acceptedRange[0])
        : startOfDay(acceptedRange[0]);
    }
    if (acceptedRange[1]) {
      _lte = targetIsDate
        ? toUTCDate(acceptedRange[1], true)
        : endOfDay(acceptedRange[1]);
    }
    if (_gte) {
      newWhere = addWhereClause(newWhere, [...targetPath, '_gte'], _gte);
    }
    if (_lte) {
      newWhere = addWhereClause(newWhere, [...targetPath, '_lte'], _lte);
    }
    setFiltersParams(onChange?.(acceptedRange, newWhere) ?? newWhere);
    handleClose();
  };

  const labelValueStart = range[0]
    ? format(range[0], 'd MMM yyyy', { locale: dateLocale })
    : '...';
  const labelValueEnd = range[1]
    ? format(range[1], 'd MMM yyyy', { locale: dateLocale })
    : '...';
  const labelValue =
    range[0] || range[1] ? `${labelValueStart} - ${labelValueEnd}` : null;
  const labelPrefix =
    hasFieldSelector && labelValue != null
      ? dateFields?.find(field => field.value === activeFieldValue)?.label ??
        label
      : label;
  const displayLabel = labelValue
    ? [labelPrefix, labelValue].join(': ')
    : labelPrefix;

  const handleDelete =
    range[0] || range[1]
      ? () => {
          setRange([null, null]);
          setFiltersParams(clearTargeted(where, activeFieldValue));
        }
      : undefined;

  const memoizedShortcutsItems = useMemo(() => shortcutsItems(t), [t]);

  return (
    <FilterChip
      label={displayLabel}
      onDelete={handleDelete}
      icon={<CalendarDays size={16} />}
      disabled={disabled?.(where) ?? false}
      renderFilter={({ handleClose }) => (
        <Paper elevation={0} sx={dateRangePickerPaperSx}>
          {hasFieldSelector && dateFields && (
            <DateFieldSelector
              fields={dateFields}
              value={selectedField}
              onChange={setSelectedField}
            />
          )}
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={dateLocale}
            localeText={{
              cancelButtonLabel: t('Cancel'),
            }}
          >
            <StaticDateRangePicker
              calendars={isMobile ? 1 : 2}
              rangePosition={rangePosition}
              onRangePositionChange={setRangePosition}
              value={range}
              slots={{
                actionBar: CustomActionBar,
                toolbar: CustomToolBar,
              }}
              slotProps={{
                shortcuts: {
                  items: memoizedShortcutsItems,
                  changeImportance: 'set',
                },
                actionBar: {
                  onCancel: () => {
                    resetDraft();
                    handleClose();
                  },
                } as CustomActionBarProps,
              }}
              onAccept={acceptedRange => {
                setRange(acceptedRange);
                handleAccept(handleClose, acceptedRange);
              }}
              onChange={([start, end]) => {
                setRange([start, end]);
              }}
              sx={getStaticDateRangePickerSx(isTablet, isMobile)}
            />
          </LocalizationProvider>
        </Paper>
      )}
    />
  );
};
